import { RiskLevel } from '@/constants/riskLevels';

export enum AgentRunStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  FINISHED = 'FINISHED',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED',
}

// The Customer Fraud Reasoning status we show in the UI incorporates various
// agents in the backend, so it needs its own status enum.
export enum CustomerFraudReasoningStatusForUi {
  PROCESSING_DOCUMENTS = 'PROCESSING_DOCUMENTS',
  ANALYZING_CUSTOMER = 'ANALYZING_CUSTOMER',
  REASONING = 'REASONING',
  FINISHED = 'FINISHED',
  FAILED = 'FAILED',
  NO_DATA = 'NO_DATA',
}

interface CustomerInsight {
  insight: string;
  insightRiskLevel: RiskLevel;
  insightType: string;
}

interface RawCustomerFraudReasoningInsightFromAPI {
  insight: string;
  insight_risk_level: RiskLevel;
  insight_type: string;
}

interface RawCustomerFraudReasoningResultFromAPI {
  id: string;
  status: 'PENDING' | 'RUNNING' | 'FINISHED' | 'CANCELLED' | 'FAILED';
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  customer_uuid: string;
  agent_risk_level: 'LOW' | 'MEDIUM' | 'HIGH' | 'NOT_APPLICABLE' | null;
  raw_agent_result: Record<string, unknown> | null;
  raw_agent_error: Record<string, unknown> | null;
  num_requests: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
}

export interface CustomerFraudReasoningResult {
  id: string;
  createdAt: string;
  status: AgentRunStatus;
  riskLevel?: RiskLevel;
  insights?: CustomerInsight[];
  startedAt?: string;
  finishedAt?: string;
}

export function parseCustomerFraudReasoningInsight(
  rawInsight: RawCustomerFraudReasoningInsightFromAPI,
): CustomerInsight {
  const { insight, insight_risk_level, insight_type } = rawInsight;
  return {
    insight,
    insightRiskLevel: insight_risk_level,
    insightType: insight_type,
  };
}

export function parseCustomerFraudReasoningResult(
  customer?: Record<string, unknown>,
): CustomerFraudReasoningResult | null | undefined {
  if (!customer) {
    return undefined;
  }

  const rawResult = customer['customer_fraud_reasoning_agent_experimental'] as
    | RawCustomerFraudReasoningResultFromAPI
    | null
    | undefined;

  if (typeof rawResult === 'undefined') {
    return undefined;
  } else if (rawResult == null) {
    // null signifies that the Customer Fraud Reasoning Agent result is
    // intentionally empty, for example when the customer has no documents.
    return null;
  }

  const parsedInsights = Array.isArray(rawResult.raw_agent_result?.insights)
    ? rawResult.raw_agent_result.insights.map(
        parseCustomerFraudReasoningInsight,
      )
    : undefined;

  return {
    id: rawResult.id,
    status: rawResult.status as AgentRunStatus,
    riskLevel: rawResult.agent_risk_level
      ? (rawResult.agent_risk_level as RiskLevel)
      : undefined,
    insights: parsedInsights,
    createdAt: rawResult.created_at,
    startedAt: rawResult.started_at ?? undefined,
    finishedAt: rawResult.finished_at ?? undefined,
  };
}

export function getCustomerFraudReasoningInsightTitle(insightType: string) {
  switch (insightType) {
    case 'TRIGGERED_DOCUMENT_FRAUD_SIGNAL':
      return 'Triggered Document Fraud Signal';
    case 'SUMMARY_SECTION_ANALYSIS':
      return 'Summary Section Analysis';
    case 'CONSISTENCY_CHECK':
      return 'Consistency Check';
    case 'AMOUNTS_RECONCILIATION':
      return 'Amounts Reconciliation';
    case 'TRANSACTIONS_ANALYSIS':
      return 'Transactions Analysis';
    case 'DOCUMENT_LAYOUT_ANALYSIS':
      return 'Document Layout Analysis';
    case 'DOCUMENT_LAYOUTS_COMPARISON':
      return 'Document Layouts Comparison';
    case 'CROSS_DOCUMENTS_CHECK':
      return 'Cross Documents Check';
    default:
      if (!insightType) {
        return '';
      }
      const convertedInsightType = insightType
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return convertedInsightType;
  }
}
